<template>
  <b-form @submit="handleSubmit" ref="form">
    <b-form-group label="Business Name"
                  label-class="text-truncate label-required"
                  v-if="organizationAttributes.includes('name')">
      <b-form-input v-model="name" required/>
    </b-form-group>

    <b-form-select v-model="industry"
                   :options="industryOptions"
                   label="Industry"
                   label-class="text-truncate label-required"
                   v-if="organizationAttributes.includes('industry')">

    </b-form-select>

    <template v-if="organizationAttributes.includes('organizationType')">
      <template v-if="organizationChangePending">
        <b-form-group label="Business Type" label-class="text-truncate">
          <b-form-select disabled
                         :options="businessTypeOptions"
                         :value="organization.account_change_request"/>
        </b-form-group>

        <div v-if="organization.account_change_request === 'agency'">
          <b-form-group label="Are you part of a holding company?" label-class="text-truncate">
            <b-form-radio-group
              v-model="isHoldingCompany"
              :options="isHoldingCompanyOptions"
              :disabled="true"
            ></b-form-radio-group>
          </b-form-group>

          <b-form-group label="Holding Company" label-class="text-truncate label-required" v-if="isHoldingCompany">
            <b-form-select disabled
                           :options="holdingCompanyOptions"
                           :value="organization.holding_company_change_request_id"/>
          </b-form-group>
        </div>

        <b-alert variant="primary" :show="true" class="mt-3">
          <div>Your request for changing the account type has been registered!</div>
        </b-alert>
      </template>

      <div v-else>
        <b-form-group label="Business Type" label-class="text-truncate label-required">
          <b-form-select v-model="organizationType"
                         :options="businessTypeOptions"
                         @change="handleOrganizationTypeChange"
                         label="Business Type"
                         label-class="text-truncate label-required"/>
        </b-form-group>

        <div v-if="organizationType === 'agency'">

          <b-form-group label="Are you part of a holding company?" label-class="text-truncate">
            <b-form-radio-group
              id="radio-group-1"
              v-model="isHoldingCompany"
              :options="isHoldingCompanyOptions"
              name="radio-options"
            ></b-form-radio-group>
          </b-form-group>

          <b-form-group label="Holding Company" label-class="text-truncate label-required" v-if="isHoldingCompany">
            <b-form-select v-model="holdingCompanyChangeRequestId"
                           :options="holdingCompanyOptions"
                           required/>
          </b-form-group>
        </div>
      </div>

    </template>


    <b-alert variant="primary" :show="organizationTypeFormChanged" class="mt-3">
      <div class="font-weight-medium">Do you want to change your account type?</div>
      <div>
        <template v-if="organizationType === 'agency'">
          Changing to an Agency account requires approval, and possibly additional onboarding steps. Choose Agency if your company provides marketing, advertising, design, production, or purchases licenses on behalf of its customers.
        </template>
        <template v-else-if="organizationType === 'enterprise'">
          Changing to an Enterprise account requires approval, and additional onboarding steps. Choose Enterprise if your company has over 1,000 employees, and annual revenues in excess of $1 billion dollars.
        </template>
      </div>
    </b-alert>

    <b-row class="mt-4 mb-2">
      <b-col class="ml-auto" cols="auto">
        <b-button variant="light"
                  class="btn-rounded px-3"
                  @click="$emit('cancel')"
                  :disabled="saving">
          Cancel
        </b-button>
        <b-button variant="primary"
                  class="btn-rounded px-3"
                  type="submit"
                  :disabled="saving || organizationChangePending || orgTypeEditorDisableConditions">
          <span v-if="saving">Please Wait...</span>
          <span v-else>Save</span>
        </b-button>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import GeneralMixins from "../../../mixins/GeneralMixins.js";
import CustomerEditProfileInfo from "../Account/EditProfileInfo.vue";
import {OrganizationsApi} from "../../Api/OrganizationsApi.js";

export default {
  components: {CustomerEditProfileInfo},
  mixins: [GeneralMixins],
  props: {
    organizationAttributes: {
      type: Array,
      required: true
    },
    accountTypeSet: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data: function () {
    return {
      saving: false,
      organization: null,
      name: null,
      industry: null,
      organizationType: null,
      industryOptions: [],
      showAccountTypeChangeWarning: false,
      holdingCompanies: [],
      isHoldingCompany: false,
      holdingCompanyChangeRequestId: null,
      isHoldingCompanyOptions: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ]
    }
  },

  async mounted() {

    try {
      const org = await OrganizationsApi.getOrganization();
      this.organization = org;
      this.name = org.name;
      this.holdingCompanyChangeRequestId = org.holding_company_change_request_id || org.holding_company_id;
      this.isHoldingCompany = !!org.holding_company_change_request_id || !!org.holding_company_id;
      this.industry = org.industry;
      this.organizationType = org.organization_type;
      const industries = await OrganizationsApi.getIndustries();
      this.industryOptions = industries.map(industry => {
        return {text: industry, value: industry}
      })

      this.holdingCompanies = await OrganizationsApi.getHoldingCompanies();
    } catch (error) {
      this.showErrorToast(this.getErrorMessages(error, true))
    }

  },

  methods: {
    handleSubmit(evt) {
      evt.preventDefault();

      if (!this.$refs.form.reportValidity()) {
        return
      }

      if (this.orgTypeEditorDisableConditions) {
        return
      }

      this.saving = true;
      OrganizationsApi.saveOrganization(this.params).then(fulfilled => {
        this.$emit('saved', this.params);
        this.saving = false;
      }).catch(error => {
        this.saving = false;
        this.showErrorToast(this.getErrorMessages(error, true))
      })
    },

    handleOrganizationTypeChange() {
      this.showAccountTypeChangeWarning = ['agency', 'enterprise'].includes(this.organizationType);
    }
  },

  watch: {
    isHoldingCompany(val) {
      if (!val) {
        this.holdingCompanyChangeRequestId = null;
      }
    }
  },

  computed: {
    orgTypeEditorDisableConditions() {
      if (this.organizationTypeEditor) {
        if (!this.accountTypeSet) {
          return false
        }

        if (!this.organizationTypeFormChanged) {
          return true
        }
      } else {
        return false
      }
    },

    organizationTypeEditor() {
      return this.organizationAttributes.includes('organizationType')
    },

    organizationTypeFormChanged(){
      if (!this.organization || !this.organizationTypeEditor) {
        return false;
      }

      return this.organizationType !== this.organization.organization_type || this.organization.holding_company_id !== this.holdingCompanyChangeRequestId;
    },
    holdingCompanyOptions() {
      return this.holdingCompanies.map(holdingCompany => {
        return {text: holdingCompany.name, value: holdingCompany.id}
      })
    },

    organizationChangePending() {
      return !!(this.organizationAttributes.includes('organizationType') &&
        this.organization &&
        this.organization.account_change_request)
    },

    businessTypeOptions() {
      return [
        {text: 'Business', value: 'business'},
        {text: 'Agency', value: 'agency'},
        {text: 'Enterprise', value: 'enterprise'},
      ]
    },

    params() {
      let params = {};

      if (this.organizationAttributes.includes('name')) {
        params.name = this.name
      }
      if (this.organizationAttributes.includes('industry')) {
        params.industry = this.industry
      }
      if (this.organizationAttributes.includes('organizationType')) {
        params.account_change_request = this.organizationType
        params.holding_company_change_request_id = this.holdingCompanyChangeRequestId
      }

      return params;
    },
  }
}
</script>